import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

const statusColors = { pending: "outline", approved: "default", rejected: "destructive", confirmed: "default" };

export default function AdminWaitlist() {
  const [entries, setEntries] = useState([]);
  const [events, setEvents] = useState([]);
  const [filterEvent, setFilterEvent] = useState("");

  const fetchEntries = useCallback(() => {
    const url = filterEvent ? `${API}/admin/waitlist?event_id=${filterEvent}` : `${API}/admin/waitlist`;
    axios.get(url, authHeaders()).then(r => setEntries(r.data)).catch(() => {});
  }, [filterEvent]);

  useEffect(() => {
    axios.get(`${API}/admin/events`, authHeaders()).then(r => setEvents(r.data)).catch(() => {});
  }, []);
  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/admin/waitlist/${id}/status`, { status }, authHeaders());
    toast.success(`Status: ${status}`); fetchEntries();
  };

  const resendWhatsApp = async (id) => {
    try {
      const r = await axios.post(`${API}/admin/waitlist/${id}/resend-whatsapp`, {}, authHeaders());
      r.data.success ? toast.success("WhatsApp sent") : toast.error(r.data.error || "Failed");
    } catch { toast.error("Failed"); }
  };

  const exportCSV = () => {
    const url = filterEvent ? `${API}/admin/waitlist/export?event_id=${filterEvent}` : `${API}/admin/waitlist/export`;
    window.open(`${url}`, "_blank");
  };

  return (
    <div data-testid="admin-waitlist">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="font-heading text-2xl tracking-tight font-medium">Waitlist ({entries.length})</h1>
        <div className="flex items-center gap-2">
          <select value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)} className="h-8 rounded-sm border border-input bg-transparent px-2 text-xs" data-testid="admin-waitlist-filter">
            <option value="">All events</option>
            {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
          </select>
          <Button variant="outline" size="sm" className="gap-1 rounded-sm text-xs h-8" onClick={exportCSV} data-testid="admin-export-waitlist"><Download className="w-3 h-3" /> CSV</Button>
        </div>
      </div>

      <div className="space-y-2">
        {entries.map((w) => (
          <div key={w.id} className="bg-card border border-border/40 rounded-sm p-4" data-testid={`admin-waitlist-${w.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-medium text-sm">{w.fullName}</span>
                  <Badge variant={statusColors[w.waitlistStatus] || "secondary"} className="text-[10px] rounded-full">{w.waitlistStatus}</Badge>
                  {w.whatsappOptIn && <Badge variant="secondary" className="text-[10px] rounded-full">WA</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">
                  {w.email} / {w.phoneNumber}
                  {w.instagramHandle && ` / IG: ${w.instagramHandle}`}
                  {w.eventName && ` / ${w.eventName}`}
                  {w.venueArea && ` / ${w.venueArea}`}
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap flex-shrink-0">
                <select defaultValue={w.waitlistStatus} onChange={(e) => updateStatus(w.id, e.target.value)} className="h-7 rounded-sm border border-input bg-transparent px-1 text-[11px]">
                  {["pending", "approved", "rejected", "confirmed"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {w.whatsappOptIn && (
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => resendWhatsApp(w.id)} title="Resend WhatsApp">
                    <MessageSquare className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
        {entries.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No waitlist entries yet</p>}
      </div>
    </div>
  );
}
